// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import _ from "es-toolkit/compat";
import { type Client, createClient } from "graphql-ws";
import {
  Environment,
  type FetchFunction,
  type GraphQLResponse,
  Network,
  Observable,
  RecordSource,
  Store,
  type SubscribeFunction,
} from "relay-runtime";

import { getApiEndpoint } from "./helpers";
import { getAuthToken } from "./hooks/auth";
import { fetchWithTimeout } from "./infra/fetchWithTimeout";

const AUTH_RETRY_DELAY_MS = 300;

const TRANSIENT_HTTP_STATUS_CODES = new Set([502, 503, 504]);

interface AuthErrorPayload {
  extensions?: {
    code: "authentication_failed" | "not_authenticated" | string;
  };
  message: string;
}

const hasAuthError = (response: GraphQLResponse): boolean => {
  if (Array.isArray(response)) return false;
  const errors = (response as { errors?: AuthErrorPayload[] }).errors;
  return !!errors?.find(
    (e) =>
      e.extensions?.code === "authentication_failed" ||
      e.extensions?.code === "not_authenticated"
  );
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const executeGraphQLRequest = async (
  headers: Record<string, string>,
  body: FormData | string
): Promise<GraphQLResponse> => {
  const res = await fetchWithTimeout("/graphql/", {
    method: "POST",
    headers,
    body,
  });

  if (!res.ok) {
    if (TRANSIENT_HTTP_STATUS_CODES.has(res.status)) {
      throw new Error(
        `GraphQL request failed: ${res.status} ${res.statusText}`
      );
    }
    try {
      return await res.json();
    } catch {
      throw new Error(
        `GraphQL request failed: ${res.status} ${res.statusText}`
      );
    }
  }

  return res.json();
};

const executeWithAuthRetry = async (
  headers: Record<string, string>,
  body: FormData | string
): Promise<GraphQLResponse> => {
  let lastAuthError = false;

  try {
    const response = await executeGraphQLRequest(headers, body);
    if (!hasAuthError(response)) {
      return response;
    }
    lastAuthError = true;
  } catch {
    // Transient/network error on first attempt — retry once
    lastAuthError = false;
  }

  await sleep(AUTH_RETRY_DELAY_MS);

  const response = await executeGraphQLRequest(headers, body);
  if (!hasAuthError(response)) {
    return response;
  }

  if (lastAuthError) {
    throw new Error("InvalidSessionToken");
  }

  throw new Error("GraphQL request failed: authentication error after retry");
};

const fetchRelay: FetchFunction = async (
  params,
  variables,
  _cacheConfig,
  uploadables
) => {
  // The Authorization: Bearer header is injected downstream by baiFetch
  // (via getFetchAuthHeaders), so no auth headers are set here.
  const headers: {
    [key: string]: string;
  } = {};

  let body: FormData | string;
  if (uploadables) {
    headers["Content-Type"] = "multipart/form-data";
    const formData = new FormData();
    formData.append(
      "operations",
      JSON.stringify({
        query: params.text,
        variables,
      })
    );

    const keys = _.keys(uploadables);
    const map = _.reduce(
      keys,
      (result, v, idx) => {
        result[idx.toString()] = [v];
        return result;
      },
      {} as {
        [key: string]: string[];
      }
    );
    formData.append("map", JSON.stringify(map));
    _.each(keys, (v, idx) => {
      formData.append(idx.toString(), uploadables[v]);
    });
    body = formData;
  } else {
    body = JSON.stringify({
      query: params.text,
      variables,
    });
  }

  return executeWithAuthRetry(headers, body);
};

// Exported for unit testing of the `?token=` contract.
export const getWsUrl = (): string => {
  // The browser's native WebSocket API cannot set an Authorization header on
  // the upgrade request, so the Bearer token is carried in the `?token=`
  // query parameter instead (read by TokenAuthMiddleware on the backend).
  const url = new URL("/ws/graphql/", getApiEndpoint());
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const token = getAuthToken();
  if (token) {
    url.searchParams.set("token", token);
  }
  return url.toString();
};

let wsClient: Client | null = null;

const getWsClient = (): Client => {
  if (!wsClient) {
    wsClient = createClient({
      url: getWsUrl(),
      keepAlive: 10_000,
      shouldRetry: (errOrCloseEvent: unknown) => {
        if (
          errOrCloseEvent &&
          typeof errOrCloseEvent === "object" &&
          "code" in errOrCloseEvent &&
          typeof errOrCloseEvent.code === "number" &&
          errOrCloseEvent.code >= 4400
        ) {
          return false;
        }
        return true;
      },
      retryAttempts: Infinity,
      retryWait: async (retries: number) => {
        const baseMs = 1000;
        const maxMs = 30_000;
        await new Promise<void>((resolve) =>
          setTimeout(resolve, Math.min(baseMs * 2 ** retries, maxMs))
        );
      },
    });
  }
  return wsClient;
};

const subscribeFn: SubscribeFunction = (operation, variables) => {
  return Observable.create((sink) => {
    const client = getWsClient();
    return client.subscribe(
      { query: operation.text ?? "", variables },
      {
        next: (value: unknown) =>
          sink.next(value as Parameters<typeof sink.next>[0]),
        error: (err: unknown) =>
          sink.error(err instanceof Error ? err : new Error(String(err))),
        complete: () => sink.complete(),
      }
    );
  });
};

// Export a singleton instance of Relay Environment
// configured with our network function:
export const createRelayEnvironment = () => {
  if (wsClient) {
    wsClient.dispose();
    wsClient = null;
  }
  return new Environment({
    network: Network.create(fetchRelay, subscribeFn),
    store: new Store(new RecordSource()),
    // handlerProvider: null
  });
};
